/**
 * Piece material registry.
 *
 * A "material" is a (roughness, metalness, emissive) PBR triple that the
 * `<PieceMesh>` applies to its `<meshStandardMaterial>` — independent of the
 * `PieceTheme`'s color, which the app's per-side color picker (Phase 3.4)
 * controls. So the player can combine a material with either color:
 * "metallic + ivory" reads as a polished marble set, "stone + dark red" reads
 * as a clay-red set, etc.
 */
export type PieceMaterialId = 'matte' | 'stone' | 'wood' | 'metallic';

export interface PieceMaterialSpec {
  readonly id: PieceMaterialId;
  readonly name: string;
  readonly roughness: number;
  readonly metalness: number;
  /** Optional self-illumination color (subtle, so dark pieces don't vanish in
   *  the unlit corners of the scene). */
  readonly emissive?: string;
  readonly emissiveIntensity?: number;
}

export const PIECE_MATERIALS: readonly PieceMaterialSpec[] = [
  // matte: default; very slight emissive so dark pieces still read in shadow
  { id: 'matte', name: 'Matte', roughness: 0.75, metalness: 0.0, emissive: '#1a1a22', emissiveIntensity: 0.08 },
  // stone: pale, fully diffuse — gives the heaviest visual weight
  { id: 'stone', name: 'Stone', roughness: 0.95, metalness: 0.0 },
  // wood: warmer diffuse with a hint of self-illumination, like polished oak
  { id: 'wood', name: 'Wood', roughness: 0.65, metalness: 0.0, emissive: '#2a1a08', emissiveIntensity: 0.05 },
  // metallic: high specular, low roughness; reads as polished marble / glass
  { id: 'metallic', name: 'Metallic', roughness: 0.25, metalness: 0.85 },
];

// Registry invariants are guarded at module init so downstream callers can
// rely on a non-empty registry without non-null asserting at every site.
function requireFirst<T>(arr: readonly T[], name: string): T {
  const first = arr[0];
  if (!first) throw new Error(`@chessmate/board-3d: ${name} registry is empty`);
  return first;
}

export const DEFAULT_PIECE_MATERIAL: PieceMaterialSpec = requireFirst(
  PIECE_MATERIALS,
  'PIECE_MATERIALS',
);

export function pieceMaterialById(id: PieceMaterialId): PieceMaterialSpec {
  return PIECE_MATERIALS.find((m) => m.id === id) ?? DEFAULT_PIECE_MATERIAL;
}

/** Compact list of swatch colors for one side's piece color picker. */
export interface PieceColorOption {
  readonly id: string;
  readonly label: string;
  readonly hex: string;
}

export const WHITE_PIECE_COLORS: readonly PieceColorOption[] = [
  { id: 'ivory', label: 'Ivory', hex: '#f4efe2' },
  { id: 'pure-white', label: 'Pure', hex: '#ffffff' },
  { id: 'pearl', label: 'Pearl', hex: '#e0e0e0' },
  { id: 'beige', label: 'Beige', hex: '#f5e6c4' },
];

export const BLACK_PIECE_COLORS: readonly PieceColorOption[] = [
  { id: 'charcoal', label: 'Charcoal', hex: '#3d3a48' },
  { id: 'dark-red', label: 'Dark Red', hex: '#5a1f1f' },
  { id: 'dark-blue', label: 'Dark Blue', hex: '#1d2e5a' },
  { id: 'graphite', label: 'Graphite', hex: '#2a2a38' },
];

export const DEFAULT_PIECE_WHITE_COLOR = WHITE_PIECE_COLORS[0]?.hex ?? '#f4efe2';
export const DEFAULT_PIECE_BLACK_COLOR = BLACK_PIECE_COLORS[0]?.hex ?? '#3d3a48';
